"use client";

import { ListingFavoriteButton } from "@/components/listings/listing-favorite-button";
import listingImagePlaceholder from "./assets/listing-placeholder.png";

import { AspectRatio } from "@/components/ui/aspect-ratio";
import type { DetailedListingListItem } from "@/lib/types";
import { getTotalAskingPrice } from "@/lib/listing-utils";
import { buildBusinessTypeImageCDNAssetUrl } from "@/lib/url";
import {
  type ImageTransformationKind,
  buildImageCDNUrl,
} from "@repo/image-transformations";
import { cn } from "@/lib/utils";
import { useFormatter, useTranslations } from "next-intl";
import Image from "@/components/common/image";
import {
  startTransition,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { Icon } from "@/components/ui/icon";
import { ListingStatusBadge } from "@/components/listings/listing-status-badge";
import { ListingPreviewAgent } from "@/components/listings/listing-preview-agent";
import { ListingPreviewInfo } from "@/components/listings/listing-preview-info";
import { ListingPreviewSeenOrContacted } from "@/components/listings/listing-preview-seen-or-contacted";
import { ListingPriceChangeBadge } from "@/components/listings/listing-price-change-badge";
import { LoaderCircleIcon } from "lucide-react";
import type { ListingInfoAttribute } from "@repo/utils";

export type ListingPreviewProps = {
  listing: DetailedListingListItem;
  modal?: boolean;
  horizontal?: boolean;
  noFavoriteButton?: boolean;
  noSeenOrContactedBadge?: boolean;
  noAgentBadge?: boolean;
  useMissingPhotosStyling?: boolean;
  renderTitle?: (title: string) => ReactNode;
  /** Optional actions row under the tile content (e.g. broker pipeline CTAs). */
  footer?: ReactNode;
  boostedAttributes?: ListingInfoAttribute[];
  className?: string;
  navigating?: boolean;
};

export function ListingPreview({
  listing,
  modal,
  horizontal,
  noFavoriteButton = false,
  noSeenOrContactedBadge = false,
  noAgentBadge = false,
  useMissingPhotosStyling = true,
  renderTitle,
  footer,
  boostedAttributes,
  className,
  navigating = false,
}: ListingPreviewProps) {
  const t = useTranslations();
  const format = useFormatter();

  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);

  const [forceSeen, setForceSeen] = useState(false);

  const isSold = listing.status === "SOLD";
  const thumbKind: ImageTransformationKind = isSold
    ? "thumbnailblurred"
    : "thumbnail";

  const imageKey = listing.listingMedia[0]?.image?.file.key;
  const imageUrl = imageKey ? buildImageCDNUrl(imageKey, thumbKind) : null;
  const placeholderImageKey = listing.placeholderImage?.file.key;
  const placeholderImageUrl = placeholderImageKey
    ? buildImageCDNUrl(placeholderImageKey, thumbKind)
    : null;
  const imageBusinessTypeFallback = listing.businessType
    ? buildImageCDNUrl(
        buildBusinessTypeImageCDNAssetUrl(listing.businessType.slug),
        "thumbnail",
      )
    : null;

  const areaName =
    listing.business?.address?.city || listing.business?.address?.state;
  const shortAddress =
    listing.locationTier === "AREA" && areaName
      ? `Somewhere in ${areaName}`
      : listing?.business?.address
        ? `${listing.business.address.city ? `${listing.business.address.city}, ` : ""}${listing.business.address.state}`
        : null;

  const title = t(
    listing.pitch
      ? "saas.listingPreview.title.normal"
      : shortAddress && listing.businessType
        ? "saas.listingPreview.title.masked"
        : "saas.listingPreview.title.untitled",
    {
      name: listing.pitch ?? "N/A",
      category: listing.businessType?.name ?? "N/A",
      address: shortAddress ?? "N/A",
    },
  );

  function nextCarouselItem(e: React.MouseEvent<HTMLButtonElement>) {
    if (!carouselApi) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    carouselApi.scrollNext();
  }

  function previousCarouselItem(e: React.MouseEvent<HTMLButtonElement>) {
    if (!carouselApi) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    carouselApi.scrollPrev();
  }

  const finalPrice = getTotalAskingPrice(listing);

  const formattedPrice = useMemo(
    () =>
      finalPrice > 0
        ? format.number(finalPrice, {
            style: "currency",
            currency: listing.currency,
            minimumFractionDigits: 0,
          })
        : t("common.negotiable"),
    [finalPrice, listing.currency, format, t],
  );

  const ariaLabel = useMemo(() => {
    const parts = [title];
    if (shortAddress) parts.push(shortAddress);
    parts.push(formattedPrice);
    return parts.join(" — ");
  }, [title, shortAddress, formattedPrice]);

  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    setCurrentCarouselIndex(carouselApi.selectedScrollSnap());

    carouselApi.on("select", () => {
      setCurrentCarouselIndex(carouselApi.selectedScrollSnap());
    });
  }, [carouselApi]);

  return (
    <article
      aria-label={ariaLabel}
      className={cn(
        "relative overflow-hidden border border-border rounded-xl bg-card transition-all duration-200 hover:border-muted-foreground/50 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] dark:hover:shadow-[0_8px_30px_rgba(0,0,0,0.3)] hover:-translate-y-0.5 h-full",
        {
          "flex flex-col": !horizontal,
          "flex flex-col md:flex-row md:items-stretch": horizontal,
        },
        className,
      )}
      onClick={() => {
        startTransition(() => setForceSeen(true));
      }}
      onKeyDown={() => {
        startTransition(() => setForceSeen(true));
      }}
    >
      <div
        className={cn("group relative overflow-hidden", {
          "md:w-[200px] md:shrink-0": horizontal,
        })}
      >
        {listing.listingMedia.length <= 1 && (
          <>
            <AspectRatio
              ratio={3 / 2}
              className={cn({ "md:hidden": horizontal })}
            >
              <Image
                src={
                  imageUrl ??
                  placeholderImageUrl ??
                  imageBusinessTypeFallback ??
                  listingImagePlaceholder
                }
                alt={title}
                fill
                className="h-full w-full object-cover"
              />
            </AspectRatio>

            {horizontal && (
              <div className="absolute inset-0 hidden md:block">
                <Image
                  src={
                    imageUrl ??
                    placeholderImageUrl ??
                    imageBusinessTypeFallback ??
                    listingImagePlaceholder
                  }
                  alt={title}
                  fill
                  className="h-full w-full object-cover"
                />
              </div>
            )}
          </>
        )}

        {listing.listingMedia.length > 1 && (
          <Carousel
            setApi={setCarouselApi}
            opts={{ watchDrag: false }}
            className={cn({
              "md:absolute md:inset-0 md:[&>div]:h-full": horizontal,
            })}
          >
            <CarouselContent className={cn({ "md:h-full": horizontal })}>
              {listing.listingMedia.map((media) => {
                if (!media.image?.file?.key) {
                  return null;
                }

                const mediaUrl = buildImageCDNUrl(
                  media.image.file.key,
                  thumbKind,
                );

                return (
                  <CarouselItem
                    key={media.id}
                    className={cn({ "md:h-full": horizontal })}
                  >
                    <AspectRatio
                      ratio={3 / 2}
                      className={cn({ "md:hidden": horizontal })}
                    >
                      <Image
                        src={mediaUrl}
                        alt={title}
                        fill
                        className="h-full w-full object-cover"
                      />
                    </AspectRatio>

                    {horizontal && (
                      <div className="relative hidden h-full md:block">
                        <Image
                          src={mediaUrl}
                          alt={title}
                          fill
                          className="h-full w-full object-cover"
                        />
                      </div>
                    )}
                  </CarouselItem>
                );
              })}
            </CarouselContent>
          </Carousel>
        )}

        {listing.listingMedia.length > 1 && (
          <>
            {carouselApi && currentCarouselIndex > 0 && (
              <div className="absolute top-1/2 left-4 -translate-y-1/2">
                <button
                  type="button"
                  aria-label={t("common.previous")}
                  className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 pointer-events-none group-hover:pointer-events-auto size-10 text-white bg-black/60 rounded-full flex items-center justify-center focus-visible:ring-2 focus-visible:ring-white"
                  onClick={previousCarouselItem}
                >
                  <Icon icon="ChevronLeft" />
                </button>
              </div>
            )}

            {carouselApi &&
              currentCarouselIndex < listing.listingMedia.length - 1 && (
                <div className="absolute top-1/2 right-4 -translate-y-1/2">
                  <button
                    type="button"
                    aria-label={t("common.next")}
                    className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 pointer-events-none group-hover:pointer-events-auto size-10 text-white bg-black/60 rounded-full flex items-center justify-center focus-visible:ring-2 focus-visible:ring-white"
                    onClick={nextCarouselItem}
                  >
                    <Icon icon="ChevronRight" />
                  </button>
                </div>
              )}
          </>
        )}

        <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
          {(!imageUrl && !placeholderImageUrl && useMissingPhotosStyling) ||
          listing.listingMedia.length > 1 ? (
            <div className="bg-black/60 text-white text-xs font-bold px-2 py-1 rounded-lg flex items-center gap-1">
              {!imageUrl && !placeholderImageUrl && useMissingPhotosStyling && (
                <>
                  <Icon icon="Lock1Solid" className="size-3" />
                  {t("saas.listingPreview.photosOnDemand")}
                </>
              )}

              {listing.listingMedia.length > 1 && (
                <span className="font-data">
                  {currentCarouselIndex + 1} / {listing.listingMedia.length}
                </span>
              )}
            </div>
          ) : (
            <div />
          )}

          {!noAgentBadge && listing.sellerAgentId && (
            <ListingPreviewAgent listing={listing} />
          )}
        </div>

        {!noFavoriteButton && (
          <div className="absolute top-3 right-3">
            <ListingFavoriteButton listingId={listing.id} />
          </div>
        )}

        {!noSeenOrContactedBadge && !isSold && (
          <div className="absolute top-3 left-3">
            <ListingPreviewSeenOrContacted
              listing={listing}
              forceSeen={forceSeen}
            />
          </div>
        )}

        {isSold && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="bg-black/60 text-white text-xs font-semibold px-3 py-1 rounded-md uppercase tracking-wide">
              Sold
            </span>
          </div>
        )}
      </div>

      <div
        className={cn("flex flex-col gap-1.5 flex-1 px-3 pt-2.5 pb-3", {
          "pb-2": modal,
        })}
      >
        <div className="flex items-center gap-1 text-muted-foreground text-xs min-w-0">
          {listing.businessType && (
            <>
              <Icon
                icon={
                  (listing.businessType.iconName
                    ? `BusinessType.${listing.businessType.iconName}`
                    : "Building2Solid") as never
                }
                className="size-3 shrink-0"
                cdnBusinessTypeIconVariant="muted"
              />
              <span className="truncate">{listing.businessType.name}</span>
            </>
          )}
          {listing.businessType && shortAddress && (
            <span className="text-muted-foreground/70">·</span>
          )}
          {shortAddress && (
            <>
              <Icon icon="MarkerPinSolid" className="size-3 shrink-0" />
              <span className="truncate">{shortAddress}</span>
            </>
          )}
        </div>

        {renderTitle?.(title)}
        {!renderTitle && (
          // Always two lines tall so the price row sits at the same height on
          // every card in a row, whether the pitch is one line or two.
          <h3
            className="min-h-10 text-sm font-semibold leading-5 line-clamp-2 text-foreground"
            title={listing.pitch ?? t("saas.listingPreview.title.untitled")}
          >
            {listing.pitch ?? t("saas.listingPreview.title.untitled")}
          </h3>
        )}

        {/* Single line: price, price change, status. Sale type lives in the details line. */}
        <div className="flex h-6 items-center gap-2 overflow-hidden whitespace-nowrap">
          <span className="text-base font-bold font-data tracking-tight text-foreground">
            {formattedPrice}
          </span>
          <ListingPriceChangeBadge
            priceDirection={listing.priceDirection}
            priceChangePercent={listing.priceChangePercent}
            visible={listing.showPriceHistory}
            compact
          />
          <ListingStatusBadge listing={listing} small />
        </div>

        <ListingPreviewInfo
          listing={listing}
          boostedAttributes={boostedAttributes}
        />

        {footer ? (
          <div
            className="mt-1.5 border-t border-border pt-2.5"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onKeyDown={(event) => event.stopPropagation()}
          >
            {footer}
          </div>
        ) : null}
      </div>

      {navigating && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-background/60 backdrop-blur-[2px]">
          <div className="absolute inset-0 overflow-hidden rounded-xl">
            <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-foreground/10 to-transparent" />
          </div>
          <div className="relative flex items-center gap-2 rounded-full bg-card/90 px-3.5 py-2 text-xs font-medium text-muted-foreground shadow-sm">
            <LoaderCircleIcon className="h-3.5 w-3.5 animate-spin" />
            Loading...
          </div>
        </div>
      )}
    </article>
  );
}
